<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import Loader from "$lib/components/loader.svelte"
  import { Button } from "$lib/components/ui/button"
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from "$lib/components/ui/dialog"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import { RadioGroup, RadioGroupItem } from "$lib/components/ui/radio-group"
  import { Textarea } from "$lib/components/ui/textarea"
  import type { _response } from "$lib/data/type/_response"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { z } from "zod/v4"
  import type { Activity } from "../_types"

  const FormSchema = z.object({
    id: z.number().default(0),
    approval_at: z.string().default(""),
    approval_status: z.string().default("0"),
    approval_by_id: z.number().default(0),
    approval_by_name: z.string().default(""),
    approval_description: z.string().default(""),
  })
  type FormData = z.infer<typeof FormSchema>
  const FormEmpty = FormSchema.parse({})

  let {
    open,
    setOpen,
    select,
    onRefresh,
  }: {
    open: boolean
    setOpen: (value: boolean) => void
    select: number
    onRefresh: () => void
  } = $props()

  const prefix = $derived(page.params.prefix ?? "0")
  const form = createForm(() => ({ defaultValues: FormEmpty }))

  const query = createQuery(() => ({
    enabled: open && select > 0,
    queryKey: ["problem-approval", prefix, select],
    queryFn: async () => {
      const response = await httpClient.get<_response<Activity>>(`/api/${prefix}/activity/problems/${select}`)
      const responseData = response.data.result!

      form.reset(FormEmpty)
      form.setFieldValue("id", responseData.id)
      form.setFieldValue("approval_at", responseData.approval_at)
      form.setFieldValue("approval_by_id", responseData.approval_by.id)
      form.setFieldValue("approval_by_name", responseData.approval_by.name)
      form.setFieldValue("approval_description", responseData.approval_description)

      return responseData
    },
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormData) => {
      const response = await httpClient.postForm<_response<string>>(`/api/${prefix}/activity/problems/${select}?approval`, { _method: "PATCH", ...data })
      const responseData = response.data

      form.reset(form.state.values)
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (previous) => ({
          ...previous,
          errorMap: { onServer: { message: messages[0] } },
        }))
      })

      if (response.status !== 200) return

      toast.success(responseData.message)
      form.reset(FormEmpty)
      setOpen(false)
      onRefresh()
    },
    onError: (error: Error) => toast.error("Terjadi kesalahan", { description: error.message }),
  }))

</script>

<Dialog
  open={open}
  onOpenChange={setOpen}>
  <DialogContent
    class="relative max-w-sm"
    onOpenAutoFocus={(event) => event.preventDefault()}>
    <DialogHeader>
      <DialogTitle>Follow Up</DialogTitle>
      <DialogDescription></DialogDescription>
    </DialogHeader>
    <Loader show={query.isFetching || mutation.isPending}/>
    <form
      action="#"
      autocomplete="off"
      class="min-w-0 space-y-6"
      onsubmit={(event) => {
        event.preventDefault()
        mutation.mutate(form.state.values)
      }}>
      <div class="space-y-3">
        <div class="flex">
          <div class="flex-1">
            <FieldLabel>Nomor</FieldLabel>
            <div class="text-desc font-mono">{query.data?.number ?? ""}</div>
          </div>
          <div class="flex-1">
            <FieldLabel>Tanggal</FieldLabel>
            <div class="text-desc font-mono">{query.data?.date.toDateFormat("dd/MM/yyyy") ?? ""}</div>
          </div>
        </div>
        <div>
          <FieldLabel>Kendala</FieldLabel>
          <div class="text-desc">{query.data?.real_problem.name ?? ""}</div>
        </div>
        <div>
          <FieldLabel>Catatan Kendala</FieldLabel>
          <div class="text-desc">{query.data ? query.data.real_problem_description || "-" : ""}</div>
        </div>
        <form.Field name="approval_at">
          {#snippet children(field)}
            <div>
              <FieldLabel>Waktu</FieldLabel>
              <Input
                name={field.name}
                value={field.state.value.toDateFormat("dd/MM/yyyy HH:mm")}
                readonly/>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="approval_by_name">
          {#snippet children(field)}
            <div>
              <FieldLabel>Oleh</FieldLabel>
              <Input
                name={field.name}
                value={field.state.value}
                readonly/>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="approval_status">
          {#snippet children(field)}
            <div>
              <FieldLabel>Status</FieldLabel>
              <RadioGroup
                name={field.name}
                value={field.state.value}
                class="flex gap-4">
                <label class="flex items-center gap-2 text-sm">
                  <RadioGroupItem
                    value="0"
                    onclick={() => field.handleChange("0")}
                  />
                  <span>diajukan</span>
                </label>
                <label class="flex items-center gap-2 text-sm">
                  <RadioGroupItem
                    value="1"
                    onclick={() => field.handleChange("1")}
                  />
                  <span>diterima</span>
                </label>
              </RadioGroup>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="approval_description">
          {#snippet children(field)}
            <div>
              <FieldLabel>Solusi</FieldLabel>
              <Textarea
                name={field.name}
                value={field.state.value}
                oninput={(event) => field.handleChange(event.currentTarget.value)}/>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          disabled={mutation.isPending}
          onclick={() => setOpen(false)}>
          Batal
        </Button>
        <Button
          type="submit"
          class="group"
          data-loading={mutation.isPending}
          disabled={mutation.isPending}>
          {#if mutation.isPending}
            <LoaderCircleIcon class="animate-spin" aria-hidden="true"/>
          {:else}
            Proses
          {/if}
        </Button>
      </DialogFooter>
    </form>
  </DialogContent>
</Dialog>
